import { action, computed, observable, makeObservable } from 'mobx';
import { Canceler } from 'axios';
import moment from 'moment';
import { defaultPagination } from 'src/constants/main';
import { IPagination, LoadingStatus } from 'src/types/main';
import { IBranchesLogFilters } from 'src/view/screens/BranchLogScreen/screens/BranchesLogScreen/types/branchesLog';
import client from 'src/services/client';
import { defaultBranchesLogFilters } from '~/view/screens/BranchLogScreen/screens/BranchesLogScreen/constants/branchesLogRow';
import { getCancelToken } from 'src/utils/helpers';
import logger from '~/services/logger';
import { dateFormatForRequest } from '~/constants/dateTime';
import { localeStore } from '~/mobx/localesStore';
import { branchStore } from '~/mobx/branchStore';

export interface IChangeDetail {
  after: any;
  before: any;
}

export type IChanges = Record<string, IChangeDetail | undefined>;

export interface IBranchesLog {
  id: number;
  model_id: number;
  changer_id: number | null;
  object_name: string;
  branch_id: number;
  branch: string;
  model_type: string;
  changes: IChanges;
  date: string;
  author: string | null;
  object: string[];
}

class BranchesLogStore {
  constructor() {
    makeObservable(this);
  }

  @observable pagination: IPagination<IBranchesLog> = defaultPagination;
  @observable filters: IBranchesLogFilters = defaultBranchesLogFilters;
  @observable public exportLoadingStatus: LoadingStatus = LoadingStatus.Init;

  BranchesLogExportAbortController = new AbortController();

  cancelStack: {
    [key: string]: Canceler;
  } = {};

  /**
   * Are there no existed branches in system
   */
  get isEmptyBranchesLogList() {
    return !this.pagination.loading && !this.pagination.data.length;
  }

  get countLines() {
    return this.pagination.data.length;
  }

  @action selectAllBranches = async () => {
    const branches = branchStore.paginationAll.data.map((branch) => branch.id);
    this.filters.branchIds = branches.length ? [0] : [];
    await this.fetchBranchesLogList();
  };

  @computed get isLoading(): boolean {
    return Boolean(this.pagination.loading || this.pagination.refreshing);
  }

  @action setFilters = async (filters: Partial<IBranchesLogFilters>) => {
    this.filters = { ...this.filters, ...filters };
    await this.fetchBranchesLogList();
  };

  @action fetchBranchesLogList = async (page: number = 1, initLoading: boolean = true) => {
    try {
      const cancelId = 'fetchBranchesLogList';
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      // skip loading if its already initialized
      if (initLoading) {
        this.pagination.loading = true;
        this.pagination.refreshing = true;
      }

      const pagination = await client.get('v3/branches/logs', {
        params: {
          page,
          ...this.getParams(),
        },
        headers: { 'Accept-Language': localeStore.language },
        cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)),
      });

      this.pagination = {
        ...pagination,
        loading: false,
        refreshing: false,
      };
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.loading = false;
      this.pagination.refreshing = false;
      logger.error({ error, place: 'BranchesLogStore fetchBranchesLogList' });
    }
  };

  clear = () => {
    this.pagination = defaultPagination;
    this.filters = defaultBranchesLogFilters;
  };

  private getParams = () => {
    const branchIds = this.filters.branchIds && !this.filters.branchIds.includes(0) ? this.filters.branchIds : null;
    return {
      sort_by: this.filters.sortBy,
      sort_dir: this.filters.sortDir,
      filters: {
        branch_ids: branchIds,
        date_start: this.formatDate(this.filters.startDate),
        date_end: this.formatDate(this.filters.endDate),
      },
    };
  };

  public formatDate = (date: number) => {
    return moment(date * 1000).format(dateFormatForRequest);
  };
}

const branchesLogStore = new BranchesLogStore();

export default branchesLogStore;
